/**
 * TierCardPreview — SA-facing sandbox for the hybrid tier card.
 *
 * Feb 2026 Hybrid Model rewrite. Renders one card per scenario so the
 * operator can eyeball every state (silver by orders, gold by points,
 * platinum by both, onyx locked-in) before flipping the flag on prod.
 *
 * URL: /preview/tier-card  (public — no login required)
 */
import { CustomerTierCard } from '../../components/CustomerTierCard';
import { useTheme } from '../../context/ThemeContext';

// Each scenario maps 1:1 to a row on the `/api/users/me/tier` response.
// `qualifying_path` matches the backend contract exactly.
const SCENARIOS = [
  {
    label: 'Standard — no activity yet',
    tier: 'standard',
    annualOrders: 0,
    annualPoints: 0,
    toNextOrders: 3,
    toNextPoints: 5000,
    nextTier: 'silver',
    qualifyingPath: null,
    pointsBalance: 25,
    isLocked: false,
  },
  {
    label: 'Silver — qualified via ORDERS only',
    tier: 'silver',
    annualOrders: 4,
    annualPoints: 1200,
    toNextOrders: 6,
    toNextPoints: 13800,
    nextTier: 'gold',
    qualifyingPath: 'orders',
    pointsBalance: 1200,
    isLocked: false,
  },
  {
    label: 'Gold — qualified via POINTS only',
    tier: 'gold',
    annualOrders: 6,
    annualPoints: 18500,
    toNextOrders: 19,
    toNextPoints: 21500,
    nextTier: 'platinum',
    qualifyingPath: 'points',
    pointsBalance: 12800,
    isLocked: false,
  },
  {
    label: 'Platinum — qualified via BOTH paths',
    tier: 'platinum',
    annualOrders: 28,
    annualPoints: 42000,
    toNextOrders: 22,
    toNextPoints: 58000,
    nextTier: 'onyx',
    qualifyingPath: 'both',
    pointsBalance: 8400,
    isLocked: false,
  },
  {
    label: 'Onyx — SA lock (grandfathered)',
    tier: 'onyx',
    annualOrders: 12,
    annualPoints: 4200,
    toNextOrders: null,
    toNextPoints: null,
    nextTier: null,
    qualifyingPath: 'orders',
    pointsBalance: 5400,
    isLocked: true,
  },
];

export function TierCardPreview() {
  const { isModern } = useTheme();
  return (
    <div className={`min-h-screen px-4 py-8 ${isModern ? "" : "bg-[#050003] text-white"}`} style={isModern ? { background: "var(--am-canvas)", color: "var(--am-ink-1)" } : undefined}>
      <div className="max-w-5xl mx-auto">
        <h1 className={isModern ? "text-2xl font-semibold mb-6" : "font-mono text-2xl mb-6 text-[#39FF14]"}>{isModern ? "Tier card preview" : "// TIER CARD · HYBRID MODEL PREVIEW"}</h1>
        {!isModern && <p className="text-sm text-[#A3C7A8] mb-8 font-mono">
          Feb 2026 hybrid tier: <span className="text-white">MAX(orders_tier, pts_tier)</span>, SA lock always wins.
          The row corresponding to <span className="text-[#39FF14]">qualifying_path</span> gets the Sparkles accent + border glow.
        </p>}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SCENARIOS.map((s, i) => (
            <div key={i} className="space-y-2">
              <div className={isModern ? "text-[11px] uppercase tracking-[0.12em] pb-1 border-b text-[var(--am-ink-3)] border-[var(--am-hair)]" : "font-mono text-[10px] uppercase tracking-[0.14em] text-[#6B8E73] pb-1 border-b border-[#2A0F3D]"}>
                {s.label}
              </div>
              <CustomerTierCard
                tier={s.tier}
                annualOrders={s.annualOrders}
                annualPoints={s.annualPoints}
                toNextOrders={s.toNextOrders}
                toNextPoints={s.toNextPoints}
                nextTier={s.nextTier}
                qualifyingPath={s.qualifyingPath}
                pointsBalance={s.pointsBalance}
                isLocked={s.isLocked}
              />
            </div>
          ))}
        </div>

        {!isModern && <div className="mt-10 text-sm text-[#6B8E73] font-mono space-y-1.5">
          <p>▸ Executive Guard: `locked_tier` on the user record wins over the hybrid computation.</p>
          <p>▸ Anti-gaming: `admin_adjust` ledger entries are excluded from the points-path sum.</p>
          <p>▸ Theme-aware: swap `&lt;html data-theme="modern"&gt;` to preview the Modern surface treatment.</p>
        </div>}
      </div>
    </div>
  );
}

export default TierCardPreview;
